import type { Page } from "./types";
export type { Page } from "./types";
import overview from "./pages/overview.md?raw";
import ui from "./pages/ui.md?raw";
import installation from "./pages/installation.md?raw";
import firstComponent from "./pages/first-component.md?raw";
import reactivity from "./pages/reactivity.md?raw";
import controlFlow from "./pages/control-flow.md?raw";
import motion from "./pages/motion.md?raw";
import scroll from "./pages/scroll.md?raw";
import routing from "./pages/routing.md?raw";
import collections from "./pages/collections.md?raw";
import synchronization from "./pages/synchronization.md?raw";
import server from "./pages/server.md?raw";
import ssr from "./pages/ssr.md?raw";
import deployment from "../../../docs/DEPLOYMENT.md?raw";
import security from "../../../docs/SECURITY.md?raw";
import validation from "../../../docs/VALIDATION.md?raw";
import implementation from "../../../docs/IMPLEMENTATION.md?raw";

// The order here controls the sidebar and previous/next navigation.
export const pages: Page[] = [
  {
    slug: "",
    group: "Start here",
    title: "Meet Zyra",
    description: "Less work for the browser. More room for your ideas.",
    demo: true,
    body: overview,
  },
  {
    slug: "installation",
    group: "Start here",
    title: "Installation",
    description: "From checkout to your first running application.",
    body: installation,
  },
  {
    slug: "first-component",
    group: "Start here",
    title: "Your first component",
    description: "Familiar TSX. Fine-grained updates.",
    demo: true,
    body: firstComponent,
  },
  {
    slug: "reactivity",
    group: "Build interfaces",
    title: "Signals & effects",
    description: "State connects directly to the work that depends on it.",
    body: reactivity,
  },
  {
    slug: "control-flow",
    group: "Build interfaces",
    title: "Lists & conditions",
    description: "Preserve the elements that matter.",
    body: controlFlow,
  },
  {
    slug: "motion",
    group: "Build interfaces",
    title: "Motion",
    description: "Make state changes feel continuous.",
    body: motion,
  },
  {
    slug: "scroll",
    group: "Build interfaces",
    title: "Scroll",
    description: "Native by default. Enhanced where it helps.",
    body: scroll,
  },
  {
    slug: "routing",
    group: "Build interfaces",
    title: "Routing",
    description: "Small client routing with native link behavior.",
    body: routing,
  },
  {
    slug: "ui",
    group: "Design system",
    title: "Zyra UI",
    description: "A component library that takes the shape of your ideas.",
    body: ui,
  },
  {
    slug: "collections",
    group: "Local-first data",
    title: "Collections",
    description: "Commit your data and its intent together.",
    body: collections,
  },
  {
    slug: "synchronization",
    group: "Local-first data",
    title: "Synchronization",
    description: "Keep the local queue honest across networks and accounts.",
    body: synchronization,
  },
  {
    slug: "server",
    group: "Ship with confidence",
    title: "Sync server",
    description: "Explicit authentication, durable storage, bounded requests.",
    body: server,
  },
  {
    slug: "ssr",
    group: "Ship with confidence",
    title: "Server rendering",
    description: "Render HTML, then adopt it in the browser.",
    body: ssr,
  },
  {
    slug: "deployment",
    group: "Ship with confidence",
    title: "Deployment",
    description: "Configuration, hosting, backups and recovery.",
    body: deployment.replace(/^# .+\r?\n/, ""),
  },
  {
    slug: "security",
    group: "Ship with confidence",
    title: "Security",
    description: "Understand the boundaries before you deploy.",
    body: security.replace(/^# .+\r?\n/, ""),
  },
  {
    slug: "validation",
    group: "Resources",
    title: "Validation",
    description: "Measured results and what still needs verification.",
    body: validation.replace(/^# .+\r?\n/, ""),
  },
  {
    slug: "implementation",
    group: "Resources",
    title: "Implementation status",
    description: "The supported release profile and the roadmap boundary.",
    body: implementation.replace(/^# .+\r?\n/, ""),
  },
];
export const href = (page: Page) =>
  "/docs" + (page.slug ? "/" + page.slug : "");

/** Find a configured guide by its stable URL slug rather than its array position. */
export function getPage(slug: string): Page {
  const page = pages.find((page) => page.slug === slug);
  if (!page) throw new Error("Unknown documentation page: " + slug);
  return page;
}
